/**
 * Tasks page — Release 1A of the Team-Task-Training spec.
 * "My Work" tab (default) + "Team view" for managers/owners.
 * Create-task dialog, per-row complete / approve / return.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, Clock, AlertCircle, Plus, Send, Undo2, Loader2,
} from "lucide-react";

const PRIORITY_STYLES = {
  low:     "bg-zinc-100 text-zinc-700 border-zinc-200",
  normal:  "bg-blue-50 text-blue-800 border-blue-200",
  high:    "bg-amber-50 text-amber-900 border-amber-300",
  urgent:  "bg-red-50 text-red-800 border-red-300",
};

const STATUS_STYLES = {
  not_started:            { label: "Not started",  cls: "bg-zinc-100 text-zinc-700" },
  in_progress:            { label: "In progress",  cls: "bg-blue-50 text-blue-800" },
  waiting:                { label: "Waiting",      cls: "bg-amber-50 text-amber-900" },
  submitted_for_review:   { label: "Awaiting review", cls: "bg-purple-50 text-purple-900" },
  completed:              { label: "Completed",    cls: "bg-emerald-50 text-emerald-800" },
  cancelled:              { label: "Cancelled",    cls: "bg-zinc-100 text-zinc-500 line-through" },
};

export default function Tasks() {
  const { user } = useAuth();
  const isManager = user && ["owner", "manager"].includes(user.role);
  const [tab, setTab] = useState("mine");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab === "mine"
        ? { mine: true, open_only: true }
        : { open_only: true };
      const r = await api.get("/tasks", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="tasks-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-950">Tasks</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Assign, track and complete team work.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden">
            <button
              onClick={() => setTab("mine")}
              className={`px-3 h-8 text-xs ${tab === "mine" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}
              data-testid="tasks-tab-mine"
            >My Work</button>
            {isManager && (
              <button
                onClick={() => setTab("team")}
                className={`px-3 h-8 text-xs border-l border-zinc-300 ${tab === "team" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}
                data-testid="tasks-tab-team"
              >Team view</button>
            )}
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="tasks-new-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New task
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-zinc-200 rounded-md text-sm text-zinc-500">
          Nothing here — create a new task or switch tabs.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((t) => (
            <TaskRow key={t.id} task={t} isManager={isManager} onChanged={load} />
          ))}
        </div>
      )}

      {showCreate && (
        <NewTaskDialog
          isManager={isManager}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}


function TaskRow({ task, isManager, onChanged }) {
  const status = STATUS_STYLES[task.status] || STATUS_STYLES.not_started;
  const pri = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.normal;
  const [busy, setBusy] = useState(false);
  const [showComments, setShowComments] = useState(false);

  async function complete() {
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/complete`, {});
      toast.success(task.requires_review && !isManager ? "Submitted for review" : "Completed");
      onChanged?.();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }
  async function approve() {
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/approve`, {});
      toast.success("Approved");
      onChanged?.();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }
  async function returnTask() {
    const reason = window.prompt("Why is this being returned?");
    if (!reason) return;
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/return`, { reason });
      toast.success("Returned to assignee");
      onChanged?.();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  return (
    <div className="border border-zinc-200 rounded-md p-3 hover:border-zinc-400 transition-colors" data-testid={`task-row-${task.id}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-950 flex items-center gap-2 flex-wrap">
            {task.title}
            <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${pri}`}>{task.priority}</span>
            <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded ${status.cls}`}>{status.label}</span>
            {task.requires_review && <Badge variant="outline" className="border-purple-300 text-purple-700 text-2xs">review required</Badge>}
          </div>
          {task.description && <div className="text-xs text-zinc-600 mt-1 line-clamp-2">{task.description}</div>}
          <div className="text-2xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
            {task.due_at && <span><Clock className="h-3 w-3 inline mr-1" />due {new Date(task.due_at).toLocaleDateString()}</span>}
            {task.client_email && <span>client: {task.client_email}</span>}
            {task.category && <span>· {task.category}</span>}
            {(task.checklist || []).length > 0 && <span>· {task.checklist.filter((c) => c.done).length}/{task.checklist.length} checked</span>}
            {task.recurrence && task.recurrence.kind !== "none" && (
              <span className="text-purple-700">· repeats {task.recurrence.kind}</span>
            )}
            <button
              onClick={() => setShowComments((v) => !v)}
              className="text-zinc-600 hover:text-zinc-900 underline text-2xs"
              data-testid={`task-comments-toggle-${task.id}`}
            >
              {showComments ? "Hide comments" : "Comments"}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {task.status === "submitted_for_review" && isManager ? (
            <>
              <Button size="sm" variant="outline" onClick={returnTask} disabled={busy} data-testid={`task-return-${task.id}`}>
                <Undo2 className="h-3.5 w-3.5 mr-1" /> Return
              </Button>
              <Button size="sm" onClick={approve} disabled={busy} className="bg-emerald-700 text-white hover:bg-emerald-800" data-testid={`task-approve-${task.id}`}>
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
              </Button>
            </>
          ) : (task.status !== "completed" && task.status !== "cancelled") ? (
            <Button size="sm" onClick={complete} disabled={busy} data-testid={`task-complete-${task.id}`}>
              {task.requires_review && !isManager
                ? <><Send className="h-3.5 w-3.5 mr-1" /> Submit</>
                : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Complete</>}
            </Button>
          ) : (
            <span className="text-2xs text-zinc-500 flex items-center gap-1">
              <Circle className="h-3 w-3" /> {task.completed_at ? new Date(task.completed_at).toLocaleDateString() : ""}
            </span>
          )}
        </div>
      </div>
      {showComments && <TaskComments taskId={task.id} />}
    </div>
  );
}


function TaskComments({ taskId }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tasks/${taskId}/comments`);
      setRows(r.data || []);
    } finally { setLoading(false); }
  }, [taskId]);
  useEffect(() => { load(); }, [load]);

  async function post() {
    const body = draft.trim();
    if (!body) return;
    setPosting(true);
    try {
      await api.post(`/tasks/${taskId}/comments`, { body });
      setDraft("");
      await load();
    } catch (e) {
      toast.error("Comment failed", { description: e?.response?.data?.detail || e.message });
    } finally { setPosting(false); }
  }

  return (
    <div className="mt-3 pt-3 border-t border-zinc-200" data-testid={`task-comments-${taskId}`}>
      {loading ? (
        <div className="text-xs text-zinc-500">Loading comments…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-zinc-500 italic">No comments yet.</div>
      ) : (
        <ul className="space-y-1.5 mb-2">
          {rows.map((c) => (
            <li key={c.id} className="text-xs" data-testid={`task-comment-${c.id}`}>
              <span className="font-medium text-zinc-800">{c.author_name}</span>
              <span className="text-zinc-500 ml-2">{new Date(c.created_at).toLocaleString()}</span>
              <div className="text-zinc-800 mt-0.5 whitespace-pre-wrap">
                {c.body}
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-start gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Write a comment. Use @name to mention a teammate."
          rows={2}
          className="flex-1 text-sm border border-zinc-300 rounded-md px-2 py-1.5"
          data-testid={`task-comment-draft-${taskId}`}
        />
        <button
          onClick={post}
          disabled={posting || !draft.trim()}
          className="text-xs h-8 px-3 rounded-md bg-zinc-950 text-white disabled:opacity-40"
          data-testid={`task-comment-post-${taskId}`}
        >
          {posting ? "Posting…" : "Post"}
        </button>
      </div>
    </div>
  );
}


function NewTaskDialog({ isManager, onClose, onSaved }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", priority: "normal",
    due_at: "", client_email: "", requires_review: false,
    assignee_user_ids: [], checklist: [],
    recurrence_kind: "none", recurrence_interval: 1,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isManager) return;
    (async () => {
      try {
        const r = await api.get("/users");
        setUsers(r.data || []);
      } catch { /* non-fatal */ }
    })();
  }, [isManager]);

  function set(patch) { setForm((f) => ({ ...f, ...patch })); }

  async function save() {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const recurrence = form.recurrence_kind === "none"
        ? { kind: "none" }
        : { kind: form.recurrence_kind, interval: Math.max(1, Number(form.recurrence_interval) || 1) };
      await api.post("/tasks", {
        ...form,
        due_at: form.due_at || null,
        client_email: form.client_email || null,
        assignee_user_ids: form.assignee_user_ids.length ? form.assignee_user_ids : undefined,
        checklist: form.checklist.filter((c) => (c.text || "").trim()),
        recurrence,
      });
      toast.success("Task created");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-task-dialog">
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
          <DialogDescription className="text-xs">
            {isManager
              ? "Assign to yourself, a team member, or a team."
              : "This task will be assigned to you (staff can only create personal to-dos)."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs">Title</Label>
            <Input value={form.title} onChange={(e) => set({ title: e.target.value })}
              placeholder="Restock resistance bands" data-testid="new-task-title" />
          </div>
          <div>
            <Label className="text-2xs">Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })} data-testid="new-task-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-2xs">Priority</Label>
              <select
                value={form.priority}
                onChange={(e) => set({ priority: e.target.value })}
                className="mt-1 h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white w-full"
                data-testid="new-task-priority"
              >
                {["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-2xs">Due date</Label>
              <Input type="date" value={form.due_at ? form.due_at.slice(0, 10) : ""}
                onChange={(e) => set({ due_at: e.target.value ? `${e.target.value}T09:00:00Z` : "" })}
                data-testid="new-task-due" />
            </div>
          </div>
          <div>
            <Label className="text-2xs">Client email (optional)</Label>
            <Input value={form.client_email} onChange={(e) => set({ client_email: e.target.value })}
              placeholder="client@example.com" data-testid="new-task-client-email" />
          </div>
          {isManager && (
            <div>
              <Label className="text-2xs">Assignee(s)</Label>
              <select
                multiple
                value={form.assignee_user_ids}
                onChange={(e) => set({ assignee_user_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                className="mt-1 min-h-[80px] px-2 py-1 border border-zinc-300 rounded-md text-sm bg-white w-full"
                data-testid="new-task-assignees"
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name || u.email} · {u.role}</option>
                ))}
              </select>
              <p className="text-2xs text-zinc-500 mt-0.5">Cmd/Ctrl-click for multiple. Empty = assign to yourself.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-2xs">Recurrence</Label>
              <select
                value={form.recurrence_kind}
                onChange={(e) => set({ recurrence_kind: e.target.value })}
                className="mt-1 h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white w-full"
                data-testid="new-task-recurrence-kind"
              >
                <option value="none">Does not repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            {form.recurrence_kind !== "none" && (
              <div>
                <Label className="text-2xs">Every</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input type="number" min={1} value={form.recurrence_interval}
                    onChange={(e) => set({ recurrence_interval: e.target.value })}
                    className="max-w-[70px]" data-testid="new-task-recurrence-interval" />
                  <span className="text-xs text-zinc-600">
                    {form.recurrence_kind === "daily" ? "day(s)" : form.recurrence_kind === "weekly" ? "week(s)" : "month(s)"}
                  </span>
                </div>
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox"
              checked={!!form.requires_review}
              onChange={(e) => set({ requires_review: e.target.checked })}
              data-testid="new-task-requires-review" />
            <span>Requires manager approval before completion</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-zinc-950 text-white" data-testid="new-task-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
